import { useCallback, useState } from "react";

export type PendingEdits = Record<string, unknown>;

/** Key a pending edit by the row's entity id and the entity field it writes. */
export const pendingEditKey = (id: number | string, field: string): string =>
  `${id}:${field}`;

const splitPendingEditKey = (key: string): { id: string; field: string } => {
  const separator = key.indexOf(":");
  return {
    id: key.slice(0, separator),
    field: key.slice(separator + 1),
  };
};

/**
 * Drop the entries the server now agrees with, keeping the rest.
 *
 * @param {object} pendingEdits Current pending edits.
 * @param {Function} serverValueFor Current server value for an id/field pair.
 * @returns {object} The same object when nothing was released, so callers can
 * use it directly as React state without looping.
 */
export function releaseSettledEdits(
  pendingEdits: PendingEdits,
  serverValueFor: (id: string, field: string) => unknown
): PendingEdits {
  const keys = Object.keys(pendingEdits);
  if (!keys.length) return pendingEdits;

  const kept: PendingEdits = {};
  keys.forEach((key) => {
    const { id, field } = splitPendingEditKey(key);
    if (serverValueFor(id, field) === pendingEdits[key]) return;
    kept[key] = pendingEdits[key];
  });

  return Object.keys(kept).length === keys.length ? pendingEdits : kept;
}

/**
 * Values committed in a grid that the server hasn't echoed back yet.
 *
 * Every task/cue mutation invalidates the query feeding the grid, so with fast
 * data entry a refetch is almost always in flight. A response that started
 * before the last few cells were written still carries their old values, and
 * rebuilding the `rows` prop from it blanks them — the number looks like it
 * never registered, so it gets typed again.
 *
 * Overlaying these values on the rows keeps a committed cell on screen until
 * the server reports it. Release is on that echo rather than on the PUT
 * resolving on purpose: committing cell A and then cell B leaves A's refetch in
 * flight, and that response predates B's write, so it must not blank B.
 *
 * @returns {object} `pendingValue` to overlay while building rows,
 * `markPending` on commit, `dropPending` when a save is refused, and
 * `releaseSettled` to call when fresh server data arrives.
 */
export function usePendingEdits(): {
  pendingEdits: PendingEdits;
  pendingValue: <T>(id: number | string, field: string, serverValue: T) => T;
  markPending: (id: number | string, field: string, value: unknown) => void;
  dropPending: (id: number | string, field: string) => void;
  releaseSettled: (
    serverValueFor: (id: string, field: string) => unknown
  ) => void;
} {
  const [pendingEdits, setPendingEdits] = useState<PendingEdits>({});

  const pendingValue = useCallback(
    <T>(id: number | string, field: string, serverValue: T): T => {
      const key = pendingEditKey(id, field);
      return key in pendingEdits ? (pendingEdits[key] as T) : serverValue;
    },
    [pendingEdits]
  );

  const markPending = useCallback(
    (id: number | string, field: string, value: unknown) => {
      setPendingEdits((current) => ({
        ...current,
        [pendingEditKey(id, field)]: value,
      }));
    },
    []
  );

  const dropPending = useCallback((id: number | string, field: string) => {
    setPendingEdits((current) => {
      const key = pendingEditKey(id, field);
      if (!(key in current)) return current;
      const kept = { ...current };
      delete kept[key];
      return kept;
    });
  }, []);

  const releaseSettled = useCallback(
    (serverValueFor: (id: string, field: string) => unknown) => {
      setPendingEdits((current) =>
        releaseSettledEdits(current, serverValueFor)
      );
    },
    []
  );

  return {
    pendingEdits,
    pendingValue,
    markPending,
    dropPending,
    releaseSettled,
  };
}

export default usePendingEdits;
